import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useChat } from '../contexts/ChatContext';
import Avatar from './Avatar';
import MessageItem from './MessageItem';
import MessageInput from './MessageInput';
import MessageSkeleton from './MessageSkeleton';
import GroupInfoModal from './GroupInfoModal';
import { IoArrowBack, IoEllipsisVertical, IoArrowDown, IoSearch, IoClose, IoPeople, IoCall } from 'react-icons/io5';
import { chatApi } from '../services/chatApi';
import './ChatWindow.css';

const ChatWindow = ({ conversation, onBack }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const {
    messages,
    setMessages,
    loadMessages,
    getMessageCount,
    getMessage,
    loadMessagesBefore,
    loadMessagesAfter,
    loadMessagesBetween,
    loadMessagesAround,
    sendMessage,
    markAsRead,
    editMessage,
    deleteMessage,
    addReaction,
    removeReaction,
    onlineUsers,
    sendTyping,
    sendStopTyping,
    getTypingUsers,
    sendRecording,
    sendStopRecording,
    getRecordingUsers,
    jumpToMessageId,
    setJumpToMessageId,
    retryMessage,
    deleteFailedMessage,
    callState,
    startCall,
  } = useChat();
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [replyTo, setReplyTo] = useState(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const prevConversationIdRef = useRef(null);
  const isInitialLoadRef = useRef(true);

  const conversationMessages = messages[conversation.id] || [];
  const typingUserIds = getTypingUsers(conversation.id);
  const recordingUserIds = getRecordingUsers(conversation.id);

  // Load messages when conversation changes
  useEffect(() => {
    let isMounted = true;

    const checkAndLoad = async () => {
      if (!isMounted) return;
      // Skip if we're jumping to a message
      if (isJumpingToMessageRef.current) return;

      const existingMessages = messages[conversation.id];
      if (!existingMessages || existingMessages.length === 0) {
        await loadInitialMessages();
      } else {
        // Messages exist - scroll to bottom with double timeout (longer delays for mobile)
        const isMobile = window.innerWidth <= 600;
        const delay = isMobile ? 400 : 50;
        const secondDelay = isMobile ? 300 : 50;

        setTimeout(() => {
          if (isMounted && !isJumpingToMessageRef.current) {
            scrollToBottom();
            setTimeout(() => {
              if (isMounted && !isJumpingToMessageRef.current) {
                scrollToBottom();
                isInitialLoadRef.current = false;
              }
            }, secondDelay);
          }
        }, delay);
      }
    };

    if (conversation.id !== prevConversationIdRef.current) {
      prevConversationIdRef.current = conversation.id;
      isInitialLoadRef.current = true;
      setReplyTo(null);

      // Load messages - check existing messages first without delay
      checkAndLoad();
    }

    return () => {
      isMounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation.id]);

  // Scroll to bottom on new messages (only if already near bottom)
  useEffect(() => {
    // Skip initial load - we handle scrolling there
    if (isInitialLoadRef.current) return;

    // Skip if user has jumped to old message (we want to stay there)
    if (hasJumpedToOldRef.current) return;

    // Skip if we're jumping to a message (in-chat search)
    if (isJumpingToMessageRef.current) return;

    // Skip if we're currently loading more messages
    if (isLoadingMoreRef.current) return;

    const container = messagesContainerRef.current;
    if (!container) return;

    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 150;
    if (isNearBottom) {
      scrollToBottom();
    }
  }, [conversationMessages.length]);

  // Mark messages as read when viewing
  useEffect(() => {
    if (conversationMessages.length === 0) return;

    const unreadIds = conversationMessages
      .filter(msg => msg.user_id !== user.id && !(msg.read_by || []).includes(user.id))
      .map(msg => msg.id);

    if (unreadIds.length > 0) {
      markAsRead(conversation.id, unreadIds);
    }
  }, [conversationMessages, conversation.id, user.id, markAsRead]);

  const loadInitialMessages = async () => {
    // Skip if we're jumping to a message
    if (isJumpingToMessageRef.current) return;

    setLoading(true);

    // Get total message count
    const totalCount = await getMessageCount(conversation.id);

    // Always load the newest messages (offset 0, DESC order)
    // Backend returns newest first, so offset 0 = newest messages
    const msgs = await loadMessages(conversation.id, 50, 0);
    setHasMore(totalCount > 50);

    setLoading(false);

    // Scroll to bottom after messages are rendered - use double timeout to ensure DOM is ready
    // Longer delays for mobile to handle slower rendering
    const isMobile = window.innerWidth <= 600;
    const delay = isMobile ? 150 : 50;
    const secondDelay = isMobile ? 100 : 50;

    setTimeout(() => {
      if (!isJumpingToMessageRef.current) {
        scrollToBottom();
      }
      setTimeout(() => {
        scrollToBottom();
        isInitialLoadRef.current = false;
      }, secondDelay);
    }, delay);
  };

  const loadMoreMessages = useCallback(async () => {
    if (!hasMore || loading) return;

    // Get the oldest message timestamp
    const oldestMsg = conversationMessages[conversationMessages.length - 1];
    if (!oldestMsg) return;

    setLoading(true);
    const beforeTime = oldestMsg.created_at;
    const msgs = await loadMessagesBefore(conversation.id, beforeTime, 50);
    setHasMore(msgs.length >= 50);
    setLoading(false);
  }, [hasMore, loading, conversationMessages, conversation.id, loadMessagesBefore]);

  const scrollToBottom = async (reload = false) => {
    const container = messagesContainerRef.current;

    if (reload) {
      // Reload fresh messages from the server
      isInitialLoadRef.current = true; // Prevent IntersectionObserver scroll restoration during reload
      setLoading(true);
      setMessages(prev => ({ ...prev, [conversation.id]: [] })); // Clear current messages
      hasJumpedToOldRef.current = false; // Clear jump flag

      const totalCount = await getMessageCount(conversation.id);
      const msgs = await loadMessages(conversation.id, 50, 0);
      setHasMore(totalCount > 50);
      setLoading(false);

      // Scroll to bottom after messages are loaded
      setTimeout(() => {
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
        setShowScrollToBottom(false);
        isInitialLoadRef.current = false;
      }, 50);
    } else {
      // Just scroll to bottom of currently loaded messages
      if (container) {
        container.scrollTop = container.scrollHeight;
        setShowScrollToBottom(false);
        // Clear jump flag when scrolled to bottom
        hasJumpedToOldRef.current = false;
      }
    }
  };

  // Track if we're currently loading to prevent rapid calls
  const isLoadingMoreRef = useRef(false);
  const loadMoreSentinelRef = useRef(null);

  // Use IntersectionObserver for more reliable auto-load
  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        // Don't auto-load during initial load - wait for user to scroll up manually
        if (entry.isIntersecting && hasMore && !loading && !isLoadingMoreRef.current && !isInitialLoadRef.current) {
          const container = messagesContainerRef.current;
          const scrollHeightBefore = container?.scrollHeight ?? 0;
          const scrollTopBefore = container?.scrollTop ?? 0;
          isLoadingMoreRef.current = true;
          loadMoreMessages().finally(() => {
            isLoadingMoreRef.current = false;
            // Restore scroll position by compensating for the added content height
            if (container) {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  const heightDiff = container.scrollHeight - scrollHeightBefore;
                  container.scrollTop = scrollTopBefore + heightDiff;
                });
              });
            }
          });
        }
      },
      { root: messagesContainerRef.current, threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadMoreMessages]);

  // Handle jump to message from cross-chat search
  useEffect(() => {
    if (!jumpToMessageId) return;

    // Mark that we've jumped to an old message - enables gap filling on scroll
    hasJumpedToOldRef.current = true;
    lastJumpTimeRef.current = Date.now();

    const messageId = jumpToMessageId;
    const container = messagesContainerRef.current;
    const convId = conversation?.id;
    if (!container || !convId) {
      setJumpToMessageId(null);
      return;
    }

    console.log('=== Jump to message ===');
    console.log('Message ID:', messageId);
    console.log('Conversation ID:', convId);

    const scrollAndHighlight = () => {
      const element = document.getElementById(`message-${messageId}`);
      if (element) {
        // Scroll to the message first
        element.scrollIntoView({
          behavior: 'auto',
          block: 'center'
        });

        // Highlight immediately (synchronously) so Playwright can catch it
        element.classList.add('highlighted');
        setTimeout(() => {
          element.classList.remove('highlighted');
        }, 4000);
        return true;
      }
      return false;
    };

    // If already visible, scroll and highlight immediately
    if (scrollAndHighlight()) {
      setJumpToMessageId(null);
      return;
    }

    // Not in current messages - load around the target message
    const loadAndScroll = async () => {
      try {
        // Load messages around the target message (30 before, 30 after)
        const response = await chatApi.getMessagesAround(convId, messageId, 30, 30);
        if (response.success && response.data) {
          // Get existing messages for this conversation
          const existingMessages = messages[convId] || [];
          const newMessages = response.data;

          // Check if target message is in the response
          const targetFound = newMessages.some(m => m.id === messageId);

          if (!targetFound) {
            console.log('Target message not found in around response, trying full load...');
            // If target not found, just set the new messages anyway
          }

          // Create a map of existing messages by ID
          const existingMap = new Map();
          existingMessages.forEach(m => existingMap.set(m.id, m));

          // Add new messages, avoiding duplicates
          const merged = [...existingMessages];
          newMessages.forEach(m => {
            if (!existingMap.has(m.id)) {
              merged.push(m);
            }
          });

          // Sort by created_at
          merged.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

          // Update messages for this conversation
          setMessages(prev => ({
            ...prev,
            [convId]: merged
          }));

          // Try to scroll after a short delay to let React render
          setTimeout(() => {
            if (scrollAndHighlight()) {
              console.log('Scrolled to message after loading');
            }
          }, 100);
        }
      } catch (err) {
        console.error('Failed to load messages around:', err);
      }

      // Clear the jump target
      setJumpToMessageId(null);
    };

    loadAndScroll();
  }, [jumpToMessageId, setJumpToMessageId, conversation?.id, messages, setMessages]);

  // Track if user has jumped to old messages
  const hasJumpedToOldRef = useRef(false);
  // Track when we last jumped (to prevent immediate reload)
  const lastJumpTimeRef = useRef(0);

  // Track last scroll position to detect scroll direction
  const lastScrollTopRef = useRef(0);

  // Track if we need to load more after current load completes
  const needsMoreLoadRef = useRef(false);

  // Track if we're currently jumping to a message (in-chat search)
  const isJumpingToMessageRef = useRef(false);

  // Fallback scroll handler (primarily for initial load detection)
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const scrollTop = container.scrollTop;
    const scrollBottom = container.scrollHeight - scrollTop - container.clientHeight;

    // Show scroll-to-bottom button only when scrolled up (more than 100px from bottom)
    setShowScrollToBottom(scrollBottom > 100);

    // If user has jumped to old message, handle gap filling - simplified
    if (hasJumpedToOldRef.current) {
      // Don't load if recently jumped (within 1.5 seconds)
      const timeSinceJump = Date.now() - lastJumpTimeRef.current;
      if (timeSinceJump < 1500) {
        lastScrollTopRef.current = scrollTop;
        return;
      }

      // Don't load if already loading
      if (isLoadingMoreRef.current || loading) {
        lastScrollTopRef.current = scrollTop;
        return;
      }

      const currentMsgs = messages[conversation.id] || [];
      if (currentMsgs.length === 0) {
        lastScrollTopRef.current = scrollTop;
        return;
      }

      // Only load when near edges
      // Scroll UP - load older messages
      if (scrollTop < 100) {
        isLoadingMoreRef.current = true;
        const scrollHeightBefore = container.scrollHeight;
        const scrollTopBefore = scrollTop;
        loadMessagesBefore(
          conversation.id,
          currentMsgs[currentMsgs.length - 1].created_at,
          50
        ).finally(() => {
          isLoadingMoreRef.current = false;
          // Restore scroll position after loading older messages - use setTimeout to ensure it happens after render
          setTimeout(() => {
            const heightDiff = container.scrollHeight - scrollHeightBefore;
            container.scrollTop = scrollTopBefore + heightDiff;
          }, 50);
        });
      }
      // Scroll DOWN - load newer messages when near bottom
      else if (scrollBottom < 100) {
        isLoadingMoreRef.current = true;
        loadMessagesAfter(
          conversation.id,
          currentMsgs[0].created_at,
          50
        ).then(msgs => {
          // Caught up to latest — exit jump mode so normal behavior resumes
          if (!msgs || msgs.length === 0) {
            hasJumpedToOldRef.current = false;
          }
        }).finally(() => {
          isLoadingMoreRef.current = false;
        });
      }

      lastScrollTopRef.current = scrollTop;
      return;
    }

    // Normal behavior: load more older messages when near top
    if (scrollTop < 50 && hasMore && !loading && !isLoadingMoreRef.current) {
      isLoadingMoreRef.current = true;
      const scrollHeightBefore = container.scrollHeight;
      const scrollTopBefore = scrollTop;
      loadMoreMessages().finally(() => {
        isLoadingMoreRef.current = false;
        // Restore scroll position by compensating for the added content height
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const heightDiff = container.scrollHeight - scrollHeightBefore;
            container.scrollTop = scrollTopBefore + heightDiff;
          });
        });
      });
    }

    lastScrollTopRef.current = scrollTop;
  }, [hasMore, loading, loadMoreMessages, loadMessagesBetween, loadMessagesAfter, loadMessagesBefore, conversation.id, messages]);

  const handleSendMessage = async (content, files = [], voiceBlob = null) => {
    try {
      await sendMessage(conversation.id, content, replyTo?.id || null, files, voiceBlob);
      setReplyTo(null);
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  const handleEditMessage = async (messageId, content) => {
    try {
      await editMessage(messageId, content);
    } catch (err) {
      console.error('Failed to edit message:', err);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await deleteMessage(messageId);
    } catch (err) {
      console.error('Failed to delete message:', err);
    }
  };

  // Handle jumping to a quoted message (in-chat search)
  const handleJumpToMessage = async (messageId) => {
    // Close search first
    handleCloseSearch();

    // Mark that we've jumped - this enables scroll-based gap filling
    hasJumpedToOldRef.current = true;
    lastJumpTimeRef.current = Date.now();

    // Load messages around the target to fill in gaps
    await loadMessagesAround(conversation.id, messageId, 30, 30);

    // Scroll to message with multiple attempts at increasing delays
    const scrollToMessage = () => {
      const el = document.getElementById(`message-${messageId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'auto', block: 'center' });
        el.classList.add('highlighted');
        setTimeout(() => el.classList.remove('highlighted'), 2000);
      }
    };

    // Try multiple times to beat the auto-scroll
    scrollToMessage();
    setTimeout(scrollToMessage, 100);
    setTimeout(scrollToMessage, 300);
    setTimeout(scrollToMessage, 500);

    // hasJumpedToOldRef is cleared automatically when loadMessagesAfter returns no results,
    // meaning we've caught up to the latest messages. User can also use scroll-to-bottom button.
  };

  // Handle search - searches both API and currently loaded messages
  const handleSearch = async (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    const trimmedQuery = query.trim().toLowerCase();
    const currentMessages = messages[conversation.id] || [];

    // First, search in currently loaded messages (instant results)
    const clientSearchResults = currentMessages.filter(msg =>
      !msg.is_deleted && msg.content &&
      msg.content.toLowerCase().includes(trimmedQuery)
    );

    setSearchLoading(true);
    try {
      // Also search the API for older messages not currently loaded
      console.log('Calling search API with query:', trimmedQuery);
      const response = await chatApi.searchMessages(conversation.id, trimmedQuery, 50, 0);
      console.log('Search API response:', response);
      if (response && response.success) {
        const apiResults = response.data || [];

        // Deduplicate each list first
        const uniqueClient = [...new Map(clientSearchResults.map(m => [m.id, m])).values()];
        const uniqueApi = [...new Map(apiResults.map(m => [m.id, m])).values()];

        // Merge results with robust deduplication
        const allResults = [...uniqueClient, ...uniqueApi];
        // Use Map to keep last occurrence (avoids duplicates)
        const uniqueMap = new Map();
        allResults.forEach(msg => uniqueMap.set(msg.id, msg));
        const mergedResults = Array.from(uniqueMap.values());
        console.log('Merged search results:', mergedResults.map(m => m.id));
        setSearchResults(mergedResults);
      } else {
        // If API fails, fall back to client-side only
        console.error('Search failed:', response?.error || 'Unknown error');
        setSearchResults(clientSearchResults);
      }
    } catch (error) {
      console.error('Search error:', error);
      // Fall back to client-side search on error
      setSearchResults(clientSearchResults);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleCloseSearch = () => {
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleReact = async (messageId, reaction) => {
    try {
      const currentMessages = messages[conversation.id] || [];
      const message = currentMessages.find(m => m.id === messageId);
      const existingReaction = message?.reactions?.find(r => r.user_id === user.id);

      let reactions;
      if (existingReaction?.reaction === reaction) {
        // User already has this reaction - remove it (toggle off)
        reactions = await removeReaction(messageId, reaction);
      } else {
        // Add new reaction (replaces any existing reaction from this user)
        reactions = await addReaction(messageId, reaction);
      }

      // Update the message in local state with new reactions
      setMessages(prev => ({
        ...prev,
        [conversation.id]: (prev[conversation.id] || []).map(msg =>
          msg.id === messageId ? { ...msg, reactions } : msg
        ),
      }));
    } catch (err) {
      console.error('Failed to add reaction:', err);
    }
  };

  const displayName = conversation.is_group
    ? conversation.name
    : conversation.other_user?.display_name || 'Unknown';
  const isOnline = !conversation.is_group && conversation.other_user && onlineUsers.has(conversation.other_user.id);

  // Get typing user names
  const typingNames = typingUserIds
    .map(uid => {
      // For 1-on-1 chats, use other_user directly
      if (!conversation.is_group && conversation.other_user?.id === uid) {
        return conversation.other_user.display_name;
      }
      const participant = conversation.participants?.find(p => p.user_id === uid);
      return participant?.user?.display_name || '';
    })
    .filter(Boolean);

  // Format typing text based on number of users
  const getTypingText = (names) => {
    if (names.length === 0) return '';
    if (names.length === 1) {
      return `${names[0]} ${t('chat.typing')}`;
    }
    if (names.length === 2) {
      return `${names[0]} and ${names[1]} ${t('chat.typingMultiple')}`;
    }
    if (names.length === 3) {
      return `${names[0]}, ${names[1]} and ${names[2]} ${t('chat.typingMultiple')}`;
    }
    // 4 or more
    return `${names[0]}, ${names[1]} ${t('chat.typingMore').replace('{count}', names.length - 2)}`;
  };

  // Get recording user names
  const recordingNames = recordingUserIds
    .map(uid => {
      if (!conversation.is_group && conversation.other_user?.id === uid) {
        return conversation.other_user.display_name;
      }
      const participant = conversation.participants?.find(p => p.user_id === uid);
      return participant?.user?.display_name || '';
    })
    .filter(Boolean);

  return (
    <div className="chat-window">
      <div className="chat-window-header">
        <div className="header-left">
          <button className="back-button" onClick={onBack}>
            <IoArrowBack />
          </button>
          <Avatar user={conversation.is_group ? null : conversation.other_user} size="small" />
          <div className="header-info">
            {!conversation.is_group && conversation.other_user ? (
              <Link to={`/profile/${conversation.other_user.id}`} className="header-name-link">
                <h3>{displayName}</h3>
              </Link>
            ) : (
              <h3>{displayName}</h3>
            )}
            {!conversation.is_group && conversation.other_user?.username && (
              <span className="header-username">@{conversation.other_user.username}</span>
            )}
            {isOnline && (
              <span className="online-status">{t('chat.online')}</span>
            )}
          </div>
        </div>
        <div className="header-right">
          {/* Group info button - only show for group chats */}
          {conversation.is_group && (
            <button className="header-action-btn" onClick={() => setShowGroupInfo(true)} title={t('chat.groupInfo')}>
              <IoPeople />
            </button>
          )}
          {/* Audio call button — hidden when a call is active */}
          {callState === 'idle' && (
            <button
              className="header-action-btn"
              onClick={() => {
                const participants = conversation.is_group
                  ? conversation.participants || []
                  : [conversation.other_user].filter(Boolean).map(u => ({
                      id: u.id,
                      display_name: u.display_name || u.username,
                    }));
                startCall(conversation.id, participants);
              }}
              title={t('call.startCall')}
            >
              <IoCall />
            </button>
          )}
          {!showSearch ? (
            <button className="header-action-btn" onClick={() => setShowSearch(true)} title={t('chat.searchMessages')}>
              <IoSearch />
            </button>
          ) : (
            <div className="chat-search-wrapper">
              <div className="chat-search-container">
                <span className="search-icon">
                  <IoSearch />
                </span>
                <input
                  type="text"
                  className="chat-search-input"
                  placeholder={t('chat.searchPlaceholder')}
                  value={searchQuery}
                  onChange={handleSearch}
                  autoFocus
                />
                {searchLoading && <span className="search-spinner"></span>}
                <button className="chat-search-close" onClick={handleCloseSearch}>
                  <IoClose />
                </button>
              </div>

              {/* Search Results Dropdown */}
              {showSearch && searchQuery.length >= 2 && (
                <div className="chat-search-dropdown">
                  {searchLoading ? (
                    <div className="search-loading">
                      <div className="search-loading-spinner"></div>
                      <span>{t('common.loading')}</span>
                    </div>
                  ) : searchResults.length > 0 ? (
                    <div className="search-results-list">
                      <div className="search-results-count">
                        {searchResults.length} {t('chat.searchResults')}
                      </div>
                      {searchResults.map(msg => (
                        <div
                          key={msg.id}
                          className="search-result-item"
                          onClick={() => {
                            handleJumpToMessage(msg.id);
                          }}
                        >
                          <div className="search-result-header">
                            <Avatar user={msg.user} size="extra-small" />
                            <span className="search-result-author">{msg.user?.display_name}</span>
                            <span className="search-result-time">
                              {new Date(msg.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="search-result-content">
                            {msg.media_urls?.length > 0 && (
                              <span className="search-result-media-badge">📷</span>
                            )}
                            {msg.voice_url && (
                              <span className="search-result-media-badge">🎤</span>
                            )}
                            <span className="search-result-text">
                              {msg.content?.trim() || (msg.voice_url ? t('chat.voiceMessage') : t('chat.image'))}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="search-no-results">
                      <IoSearch className="search-no-results-icon" />
                      <span>{t('chat.noSearchResults')}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div
        className="chat-messages-container"
        ref={messagesContainerRef}
        onScroll={handleScroll}
      >
        {loading && <MessageSkeleton count={6} />}

        {/* Sentinel for IntersectionObserver auto-load */}
        {hasMore && (
          <div
            ref={loadMoreSentinelRef}
            className="load-more-sentinel"
            style={{ height: '1px' }}
          />
        )}

        {hasMore && !loading && conversationMessages.length > 0 && (
          <button className="load-more-messages" onClick={loadMoreMessages}>
            {t('chat.loadMore')}
          </button>
        )}

        {conversationMessages.length === 0 && !loading && (
          <div className="messages-empty">
            <p>{t('chat.noMessages')}</p>
          </div>
        )}

        <div className="chat-messages">
          {[...conversationMessages].reverse().map((message, index, arr) => {
            const prevMsg = index > 0 ? arr[index - 1] : null;
            const showAvatar = !prevMsg || prevMsg.user_id !== message.user_id;

            return (
              <MessageItem
                key={message.id}
                message={message}
                isOwn={message.user_id === user.id}
                showAvatar={showAvatar}
                currentUserId={user.id}
                conversationId={conversation.id}
                onReply={() => setReplyTo(message)}
                onEdit={handleEditMessage}
                onDelete={handleDeleteMessage}
                onReact={handleReact}
                onJumpToMessage={handleJumpToMessage}
                onRetry={retryMessage}
                onDeleteFailed={deleteFailedMessage}
              />
            );
          })}
          {recordingNames.length > 0 ? (
            <div className="typing-indicator">
              <span className="typing-label">{recordingNames.join(', ')} {t('chat.recording')}</span>
            </div>
          ) : typingNames.length > 0 && (
            <div className="typing-indicator">
              <span className="typing-label">{getTypingText(typingNames)}</span>
              <span className="typing-dots">
                <span></span>
                <span></span>
                <span></span>
              </span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Scroll to bottom button - only show when scrolled up (not at bottom) */}
        {showScrollToBottom && (
          <button className="scroll-to-bottom" onClick={() => scrollToBottom(true)} title={t('chat.scrollToBottom')}>
            <IoArrowDown />
          </button>
        )}
      </div>

      <MessageInput
        onSend={handleSendMessage}
        onTyping={() => sendTyping(conversation.id)}
        onStopTyping={() => sendStopTyping(conversation.id)}
        onRecording={() => sendRecording(conversation.id)}
        onStopRecording={() => sendStopRecording(conversation.id)}
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
        disabled={loading}
      />

      {/* Group Info Modal */}
      {showGroupInfo && (
        <GroupInfoModal
          conversation={conversation}
          onClose={() => setShowGroupInfo(false)}
          onUpdated={() => {
            // Refresh is handled by loadConversations in the modal
          }}
        />
      )}
    </div>
  );
};

export default ChatWindow;
